import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Simple resend cooldown timer.
 *
 * Contract:
 * - Call `start()` to begin a countdown.
 * - `secondsLeft` counts down to 0.
 * - `isCoolingDown` is true while secondsLeft > 0.
 * - Automatically clears the interval on unmount.
 */
export default function useResendCooldown(defaultSeconds = 60) {
  const [secondsLeft, setSecondsLeft] = useState(0)
  const intervalRef = useRef(null)

  const clear = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const start = useCallback(
    (seconds = defaultSeconds) => {
      clear()
      setSecondsLeft(seconds)

      intervalRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clear()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    },
    [clear, defaultSeconds]
  )

  useEffect(() => {
    return () => {
      clear()
    }
  }, [clear])

  return {
    secondsLeft,
    isCoolingDown: secondsLeft > 0,
    start,
    clear,
  }
}
